import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// Context
import { ThemeProvider } from "./context/ThemeContext";

// Layout Components
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";

// Auth Components
import AuthModal from "./components/auth/AuthModal";
import AuthCallback from "./components/auth/AuthCallback";

// Pages
import HomePage from "./pages/HomePage";
import MarketplacePage from "./pages/MarketplacePage";
import CommunityPage from "./pages/CommunityPage";
import RankingPage from "./pages/RankingPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import TorneosPage from "./pages/TorneosPage";
import PublicProfilePage from "./pages/PublicProfilePage";
import FormatosPage from "./pages/FormatosPage";
import RulingsPage from "./pages/RulingsPage";
import BanlistPage from "./pages/BanlistPage";
import CardDatabase from "./pages/EdisonCardDatabase";
import NotFoundPage from "./pages/NotFoundPage";
import UserProfilePage from "./pages/UserProfilePage";
import DeckManagerPage from "./pages/DeckManagerPage";
import DeckBuilderPage from "./pages/DeckBuilderPage";
import StoresPage from "./pages/StoresPage";
import NewsPage from "./pages/NewsPage";
import NewsDetailPage from "./pages/NewsDetailPage";
import TorneoDetailPage from "./pages/TorneoDetailPage";
import ProtoTCGPage from "./pages/ProtoTCGPage";
import PlayerProfilePage from "./pages/PlayerProfilePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Local storage keys
const CART_KEY = 'nirvana_cart';

function AppContent() {
  const [cartItems, setCartItems] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [nirtcgBalance, setNirtcgBalance] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check for session_id in URL (Google OAuth callback)
  useEffect(() => {
    if (location.hash?.includes('session_id=')) {
      // Let AuthCallback handle this
      return;
    }
  }, [location]);

  // Check existing session on mount
  useEffect(() => {
    checkAuth();
    loadCartFromStorage();
  }, []);

  // Sync cart to localStorage when it changes
  useEffect(() => {
    if (cartItems.length > 0 || authChecked) {
      localStorage.setItem(CART_KEY, JSON.stringify(cartItems));
    }
  }, [cartItems, authChecked]);

  // Sync cart with server when logged in
  useEffect(() => {
    if (isLoggedIn && user?.user_id) {
      syncCartWithServer();
    }
  }, [isLoggedIn, user?.user_id]);

  // Polling for balance updates
  useEffect(() => {
    if (!isLoggedIn || !user?.user_id) return;
    const fetchBalance = async () => {
      try {
        const res = await axios.get(`${API}/nircoin/balance`, { withCredentials: true });
        setNirtcgBalance(res.data.balance);
      } catch (e) { /* ignore */ }
    };
    fetchBalance();
    const interval = setInterval(fetchBalance, 30000);
    return () => clearInterval(interval);
  }, [isLoggedIn, user?.user_id]);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setIsLoggedIn(true);
      setUser(res.data);
      setNirtcgBalance(res.data.nircoin_balance || 0);
    } catch (e) {
      setIsLoggedIn(false);
      setUser(null);
    }
    setAuthChecked(true);
  };

  const loadCartFromStorage = () => {
    const savedCart = localStorage.getItem(CART_KEY);
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Error loading cart:', e);
      }
    }
  };

  const syncCartWithServer = async () => {
    try {
      const localCart = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
      if (localCart.length > 0) {
        const res = await axios.post(`${API}/cart/sync`, { items: localCart }, { withCredentials: true });
        setCartItems(res.data.items || []);
      } else {
        const res = await axios.get(`${API}/cart`, { withCredentials: true });
        if (res.data.items?.length > 0) {
          setCartItems(res.data.items);
        }
      }
    } catch (e) {
      console.error('Error syncing cart:', e);
    }
  };

  const handleAddToCart = async (product) => {
    const newItem = {
      product_id: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      image: product.image,
      section: product.section || 'public'
    };

    // Check if item already exists
    const existingIndex = cartItems.findIndex(item => item.product_id === product.id);
    let newCart;
    
    if (existingIndex >= 0) {
      newCart = cartItems.map((item, index) => 
        index === existingIndex ? { ...item, quantity: item.quantity + 1 } : item
      );
    } else {
      newCart = [...cartItems, newItem];
    }
    
    setCartItems(newCart);

    // Sync with server if logged in
    if (isLoggedIn) {
      try {
        await axios.post(`${API}/cart/add`, newItem, { withCredentials: true });
      } catch (e) {
        console.error('Error adding to cart:', e);
      }
    }
  };

  const handleAuthSuccess = (userData) => {
    setIsLoggedIn(true);
    setUser(userData);
    setNirtcgBalance(userData.nircoin_balance || 0);
    syncCartWithServer();
  };

  const handleLogout = async () => { 
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout error:', e);
    }
    setIsLoggedIn(false); 
    setUser(null); 
    setNirtcgBalance(0);
    // Keep local cart
  };

  // Handle auth callback route
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onAuthSuccess={handleAuthSuccess} />;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Header 
        cartCount={cartItems.reduce((acc, item) => acc + (item.quantity || 1), 0)} 
        cartItems={cartItems} 
        isLoggedIn={isLoggedIn} 
        user={user} 
        onLogout={handleLogout} 
        nirtcgBalance={nirtcgBalance}
        onOpenAuthModal={() => setShowAuthModal(true)}
      />
      <main className="pt-16">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/marketplace" element={
            <MarketplacePage 
              onAddToCart={handleAddToCart} 
              isLoggedIn={isLoggedIn}
              nirtcgBalance={nirtcgBalance}
            />
          } />
          <Route path="/comunidad" element={<CommunityPage isLoggedIn={isLoggedIn} user={user} />} />
          <Route path="/ranking" element={<RankingPage user={user} />} />
          <Route path="/torneos" element={<TorneosPage />} />
          <Route path="/torneos/:id" element={<TorneoDetailPage user={user} />} />
          <Route path="/tiendas" element={<StoresPage />} />
          <Route path="/noticias" element={<NewsPage />} />
          <Route path="/noticias/:slug" element={<NewsDetailPage />} />
          <Route path="/juegos/proto-tcg" element={<ProtoTCGPage />} />
          <Route path="/jugador/:name" element={<PlayerProfilePage />} />
          <Route path="/formatos" element={<FormatosPage />} />
          <Route path="/rulings" element={<RulingsPage />} />
          <Route path="/banlist" element={<BanlistPage />} />
          <Route path="/edison-cards" element={<CardDatabase />} />
          <Route path="/admin" element={<AdminPage user={user} authChecked={authChecked} />} />
          <Route path="/users/:userId" element={<PublicProfilePage currentUser={user} isLoggedIn={isLoggedIn} />} />
          <Route path="/profile/:userId" element={<UserProfilePage />} />
          <Route path="/decks" element={<DeckManagerPage />} />
          <Route path="/deck-builder/:deckId" element={<DeckBuilderPage />} />
          <Route path="/deck-builder" element={<DeckBuilderPage />} />
          <Route path="/perfil" element={
            <ProfilePage 
              user={user} 
              nirtcgBalance={nirtcgBalance} 
              isLoggedIn={isLoggedIn}
              onOpenAuthModal={() => setShowAuthModal(true)}
            />
          } />
          <Route path="/auth/callback" element={<AuthCallback onAuthSuccess={handleAuthSuccess} />} />
          <Route path="/wallet" element={
            <WalletPage 
              user={user} 
              nirtcgBalance={nirtcgBalance} 
              isLoggedIn={isLoggedIn}
              onOpenAuthModal={() => setShowAuthModal(true)}
            />
          } />
          <Route path="/cart" element={
            <CartPage 
              cartItems={cartItems}
              setCartItems={setCartItems}
              isLoggedIn={isLoggedIn}
              nirtcgBalance={nirtcgBalance}
              onOpenAuthModal={() => setShowAuthModal(true)}
            />
          } />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={handleAuthSuccess}
      />
    </div>
  );
}

// Wallet Page Component
const WalletPage = ({ user, nirtcgBalance, isLoggedIn, onOpenAuthModal }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isLoggedIn) loadTransactions();
  }, [isLoggedIn]);

  const loadTransactions = async () => {
    try {
      const res = await axios.get(`${API}/nircoin/transactions`, { withCredentials: true });
      setTransactions(res.data.transactions || []);
    } catch (e) {
      console.error('Error loading transactions:', e);
    }
    setLoading(false);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-black pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Acceso Requerido</h1>
          <p className="text-gray-400 mb-6">Inicia sesión para ver tu billetera</p>
          <button onClick={onOpenAuthModal} className="px-6 py-3 bg-gradient-to-r from-pink-500 to-red-500 rounded-full text-white font-semibold">
            Iniciar Sesión
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', { 
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'purchase': return '🛒';
      case 'reward': return '🎁';
      case 'tournament_entry': return '🏆';
      case 'refund': return '↩️';
      default: return '💰';
    }
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <h1 className="text-3xl font-black text-center mb-8">
          <span className="bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">
            Mis NirCoins
          </span>
        </h1>

        {/* Balance Card */}
        <div className="glass-card rounded-2xl p-8 text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
            <span className="text-3xl font-black text-white">N</span>
          </div>
          <p className="text-gray-400 mb-2">Saldo Actual</p>
          <p className="text-5xl font-black text-yellow-400">{nirtcgBalance?.toFixed(2) || '0.00'}</p>
          <p className="text-gray-400 mt-2">NirCoins</p>
        </div>

        {/* Transactions */}
        <div className="glass-card rounded-2xl p-6">
          <h2 className="text-xl font-bold text-yellow-400 mb-4">Historial de Transacciones</h2>
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : transactions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No hay transacciones aún</p>
          ) : (
            <div className="space-y-3">
              {transactions.map(tx => (
                <div key={tx.id} className="flex items-center gap-4 p-3 bg-purple-900/30 rounded-lg">
                  <span className="text-2xl">{getTypeIcon(tx.type)}</span>
                  <div className="flex-1">
                    <p className="font-semibold text-white text-sm">{tx.description}</p>
                    <p className="text-xs text-gray-500">{formatDate(tx.created_at)}</p>
                  </div>
                  <span className={`font-bold ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Cart Page Component
const CartPage = ({ cartItems, setCartItems, isLoggedIn, nirtcgBalance, onOpenAuthModal }) => {
  const [payWithNirCoin, setPayWithNirCoin] = useState(false);

  const total = cartItems.reduce((acc, item) => acc + (item.price * (item.quantity || 1)), 0);
  const totalNirCoin = cartItems.reduce((acc, item) => acc + ((item.price_nircoin || item.price * 2) * (item.quantity || 1)), 0);

  const updateQuantity = (productId, delta) => {
    setCartItems(cartItems.map(item => {
      if (item.product_id === productId) {
        const newQty = Math.max(1, (item.quantity || 1) + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const removeItem = (productId) => {
    setCartItems(cartItems.filter(item => item.product_id !== productId));
    if (isLoggedIn) {
      axios.delete(`${API}/cart/remove/${productId}`, { withCredentials: true }).catch(console.error);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-3xl font-black text-center mb-8">
          <span className="bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">
            Tu Carrito
          </span>
        </h1>

        {cartItems.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center">
            <span className="text-6xl mb-4 block">🛒</span>
            <p className="text-gray-400 text-lg mb-6">Tu carrito está vacío</p>
            <a href="/marketplace" className="inline-block px-6 py-3 bg-gradient-to-r from-pink-500 to-red-500 rounded-full text-white font-semibold">
              Explorar Marketplace
            </a>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map(item => (
                <div key={item.product_id} className="glass-card rounded-xl p-4 flex gap-4">
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-purple-900/50 flex-shrink-0">
                    {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">{item.name}</h3>
                    <p className="text-pink-400 font-bold">${item.price}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <button onClick={() => removeItem(item.product_id)} className="text-red-400 hover:text-red-300 text-sm">
                      Eliminar
                    </button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQuantity(item.product_id, -1)} className="w-8 h-8 rounded-full border border-gray-600 text-white hover:bg-gray-800">-</button>
                      <span className="text-white font-semibold w-8 text-center">{item.quantity || 1}</span>
                      <button onClick={() => updateQuantity(item.product_id, 1)} className="w-8 h-8 rounded-full border border-gray-600 text-white hover:bg-gray-800">+</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="glass-card rounded-xl p-6 h-fit sticky top-24">
              <h2 className="text-xl font-bold text-yellow-400 mb-4">Resumen</h2>
              
              {isLoggedIn && (
                <label className="flex items-center gap-3 mb-4 cursor-pointer p-3 bg-gradient-to-r from-amber-900/30 to-yellow-900/30 border border-yellow-600/30 rounded-lg">
                  <input 
                    type="checkbox"
                    checked={payWithNirCoin}
                    onChange={(e) => setPayWithNirCoin(e.target.checked)}
                    className="w-5 h-5 rounded accent-yellow-400"
                  />
                  <div>
                    <span className="text-sm font-semibold text-yellow-400">Pagar con NirCoins</span>
                    <p className="text-xs text-gray-400">Saldo: {nirtcgBalance?.toFixed(2)}</p>
                  </div>
                </label>
              )}

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-gray-400">
                  <span>Subtotal</span>
                  <span>{payWithNirCoin ? `${totalNirCoin} NC` : `$${total.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Envío</span>
                  <span>Por calcular</span>
                </div>
              </div>
              
              <div className="border-t border-purple-700 pt-4 mb-4">
                <div className="flex justify-between text-xl font-bold">
                  <span className="text-white">Total</span>
                  <span className={payWithNirCoin ? 'text-yellow-400' : 'text-pink-400'}>
                    {payWithNirCoin ? `${totalNirCoin} NC` : `$${total.toFixed(2)}`}
                  </span>
                </div>
              </div>

              {isLoggedIn ? (
                <button className="w-full py-3 bg-gradient-to-r from-pink-500 to-red-500 rounded-lg font-semibold text-white hover:shadow-lg hover:shadow-pink-500/30 transition-all">
                  Proceder al Pago
                </button>
              ) : (
                <button onClick={onOpenAuthModal} className="w-full py-3 bg-gradient-to-r from-pink-500 to-red-500 rounded-lg font-semibold text-white">
                  Inicia Sesión para Comprar
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  useEffect(() => {
    axios.get(`${API}/`).then(res => console.log(res.data.message)).catch(e => console.error(e));
  }, []);

  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
